import React, { useMemo } from "react";
import { Stack } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import { useGetOneNusukCardsReturnQuery } from "@/services/nusukCards/nusukCards";
import {
  mergeReturnRow,
  pickCampaignOrGroupName,
  pickCampaignOrGroupNumber,
  pickNationality,
  pickOrganizer,
  pickServiceCenter,
  unwrapReturnPayload,
} from "./nusukReturnRecord";

const NusukCardsReturnDetails = () => {
  const { t } = useTranslation();
  const { returnId } = useParams();
  const { currentSystem } = useSystem();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();

  const returnsListLink = `/app/${currentSystem}/nusuk-cards/returns`;

  const { data, isLoading, isError } = useGetOneNusukCardsReturnQuery(returnId);
  const row = useMemo(
    () => mergeReturnRow(unwrapReturnPayload(data)),
    [data]
  );

  const organizerLabel = pickOrganizer(row, getLocalizedText);

  const receiptDate =
    row?.received_at ?? row?.receipt_date ?? row?.return_receipt_at ?? null;

  if (isLoading) {
    return (
      <Stack gap={2}>
        <PageHeader title={t("VIEW")} backTo={returnsListLink} sticky={false} />
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner />
        </Container>
      </Stack>
    );
  }

  if (isError || !row) {
    return (
      <Stack gap={2}>
        <PageHeader title={t("VIEW")} backTo={returnsListLink} sticky={false} />
        <Container fluid>
          <p className="text-muted mb-3">{t("NO_DATA")}</p>
          <Link to={returnsListLink} className="btn btn-primary">
            {t("BACK")}
          </Link>
        </Container>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <PageHeader title={t("VIEW")} backTo={returnsListLink} sticky={false} />
      <Container fluid>
        <div className="card border rounded-3">
          <div className="card-body py-3 px-4">
            <Row className="g-3">
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("NUSUK_BATCH_NUMBER")}
                  value={row?.batch_no ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("CAMPAIGN_OR_GROUP_NUMBER")}
                  value={pickCampaignOrGroupNumber(row)}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("CAMPAIGN_OR_GROUP_NAME")}
                  value={pickCampaignOrGroupName(row, getLocalizedText)}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("SERVICE_CENTER")}
                  value={pickServiceCenter(row, getLocalizedText)}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("ORGANIZER")}
                  value={organizerLabel}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("NATIONALITY")}
                  value={pickNationality(row, getLocalizedText)}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("GROUP_NAME")}
                  value={row?.group_name ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("NUSUK_CARDS_COUNT")}
                  value={row?.card_no ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("NUSUK_RETURN_REASON_COL")}
                  value={
                    row?.reason ??
                    row?.return_reason ??
                    row?.reason_text ??
                    "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("NUSUK_COMPLETE_CASE_RECIPIENT")}
                  value={
                    row?.representative_name ??
                    row?.recipient_name ??
                    "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4}>
                <LabelValue
                  label={t("NUSUK_RECEIPT_DATE")}
                  value={
                    receiptDate
                      ? getLocalizedDate(receiptDate, DATE_TIME_FORMAT)
                      : "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12}>
                <LabelValue
                  label={t("NOTES")}
                  value={row?.notes ?? "-"}
                  direction="vertical"
                />
              </Col>
            </Row>
          </div>
        </div>
      </Container>
    </Stack>
  );
};

export default NusukCardsReturnDetails;
